import {useWindowSize} from '@wandb/weave/common/hooks/useWindowSize';
import {Icon, type IconName} from '@wandb/weave/components/Icon';
import {
  ExpandingPill,
  getTagColorClass,
  getTagHoverClass,
  IconOnlyPill,
  type TagColorName,
} from '@wandb/weave/components/Tag';
import React from 'react';
import {twMerge} from 'tailwind-merge';

import {namedWorkspaceBelongsToUser} from '../../../state/workspaces/types';
import {DD_PRIVACY_ALLOW} from '../../../util/datadog/privacy';
import {useStatsigGatePersonalSavedViews} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {useShouldAutoGeneratePanels} from '../../WorkspaceDrawer/Settings/hooks';
import {useViewBarContext} from '../NamedWorkspaceViewBarContext';
import {NamedWorkspaceTooltip} from './NamedWorkspaceTooltip';
import {UserProfileImage} from './UserProfileImage';

type PillWithTooltipProps = {
  tooltipContent: string;
  tooltipWidth: string;
  dataTest: string;
  children: React.ReactNode;
};

export const PillWithTooltip = ({
  tooltipContent,
  tooltipWidth,
  dataTest,
  children,
}: PillWithTooltipProps) => {
  return (
    <NamedWorkspaceTooltip
      tooltipContent={tooltipContent}
      tooltipWidth={tooltipWidth}
      dataTest={dataTest}
      contentProps={{
        side: 'bottom',
        align: 'center',
        collisionPadding: 68, // 56px (menu) + 12 padding // TODO - handle mobile
        sideOffset: 12,
      }}>
      {children}
    </NamedWorkspaceTooltip>
  );
};

// TODO - look at launch pages and see if we can reuse because they have a similar pill with Image
const OWN_WORKSPACE_TOOLTIP =
  'This is your personal exploration space. Changes you make are auto-saved.';
export const PersonalPill = () => {
  const {currentWorkspaceNwId, viewer, currentView} = useViewBarContext();

  const doesBelongToUser = namedWorkspaceBelongsToUser(
    currentWorkspaceNwId,
    viewer
  );
  const text = doesBelongToUser
    ? OWN_WORKSPACE_TOOLTIP
    : currentWorkspaceNwId.nwType !== 'personal workspace'
      ? 'Changes you make will be discarded at the end of the session.'
      : `This is ${currentWorkspaceNwId.username}’s personal exploration space. Changes you make will be discarded at the end of the session.`;

  return (
    <PillWithTooltip
      dataTest="nw-personal-pill"
      tooltipContent={text}
      tooltipWidth="16rem">
      <div
        data-dd-action-name="Personal workspace pill"
        data-dd-privacy={DD_PRIVACY_ALLOW}
        className={twMerge(
          'min-h-22 flex max-h-22 w-max items-center rounded-2xl',
          'gap-6 sm:pr-7',
          getTagColorClass('moon'),
          getTagHoverClass('moon')
        )}>
        <UserProfileImage
          src={currentView?.user.photoUrl}
          alt={currentView?.user.name}
        />
        <p
          className={twMerge(
            'hidden whitespace-nowrap sm:contents',
            'text-sm text-wb-text-primary dark:text-moon-200'
          )}>
          Personal workspace
        </p>
      </div>
    </PillWithTooltip>
  );
};

// Note that this combines the Pill and IconOnlyPill to account for responsive styling
export const SharedPill = () => {
  return (
    <PillWithTooltip
      dataTest="nw-saved-pill"
      tooltipContent="Any team member can use this view. Unsaved changes will be discarded at the end of the session."
      tooltipWidth="16rem">
      <div
        data-dd-action-name="Shared workspace pill"
        data-dd-privacy={DD_PRIVACY_ALLOW}
        className={twMerge(
          'min-h-22 flex max-h-22 w-fit items-center rounded-2xl p-4',
          'sm:pl-4 sm:pr-7 sm:text-sm',
          getTagColorClass('magenta'),
          getTagHoverClass('magenta')
        )}>
        <Icon
          className="m-4 h-14 w-14 shrink-0 grow-0 md:mr-4"
          name="view-glasses"
        />
        <span
          className={twMerge(
            'hidden sm:contents',
            'max-w-[24ch] overflow-hidden text-ellipsis whitespace-nowrap'
          )}>
          Saved view
        </span>
      </div>
    </PillWithTooltip>
  );
};

const LegacyNamedWorkspaceTypePills = () => {
  const {workspaceType} = useViewBarContext();

  return workspaceType === 'personal' ? <PersonalPill /> : <SharedPill />;
};

const EditScopedNamedWorkspaceTypePill = () => {
  const {currentView, viewer} = useViewBarContext();
  if (currentView == null) {
    return null;
  }

  const isOwnerOnly = currentView.locked;
  const label = isOwnerOnly ? 'Personal view' : 'Team view';
  const ownershipTooltip =
    currentView.user.id === viewer?.id
      ? 'You created this view.'
      : `${currentView.user.name} created this view.`;
  const editScopeTooltip = currentView.locked
    ? 'Only the creator can edit it.'
    : 'Teammates with project access can edit it.';

  return (
    <PillWithTooltip
      dataTest={isOwnerOnly ? 'nw-personal-view-pill' : 'nw-team-view-pill'}
      tooltipContent={`${ownershipTooltip} ${editScopeTooltip}`}
      tooltipWidth="16rem">
      <div
        data-dd-action-name={`${label} pill`}
        data-dd-privacy={DD_PRIVACY_ALLOW}
        className={twMerge(
          'min-h-22 flex max-h-22 w-max items-center rounded-2xl',
          'gap-6 sm:pr-7',
          getTagColorClass('moon'),
          getTagHoverClass('moon')
        )}>
        <UserProfileImage
          src={currentView.user.photoUrl}
          alt={currentView.user.name}
        />
        <p
          className={twMerge(
            'hidden whitespace-nowrap sm:contents',
            'text-sm text-wb-text-primary'
          )}>
          {label}
        </p>
      </div>
    </PillWithTooltip>
  );
};

export const NamedWorkspaceTypePills = () => {
  const {currentView} = useViewBarContext();
  const {isPersonalSavedViewsEnabled, isLoading} =
    useStatsigGatePersonalSavedViews(currentView?.project?.entityName);
  if (currentView?.project?.entityName == null || isLoading) {
    return null;
  }

  return isPersonalSavedViewsEnabled ? (
    <EditScopedNamedWorkspaceTypePill />
  ) : (
    <LegacyNamedWorkspaceTypePills />
  );
};

export const PanelGenerationIconPill = () => {
  const {responsiveSize} = useWindowSize();
  const shouldAutoGeneratePanels = useShouldAutoGeneratePanels();

  if (shouldAutoGeneratePanels === 'pending') {
    return null;
  }

  const label = shouldAutoGeneratePanels
    ? 'Automated workspace'
    : 'Manual workspace';

  const tooltip = shouldAutoGeneratePanels
    ? 'Panels are automatically generated for all keys logged in this project. You can switch to a manual workspace by resetting the workspace.'
    : 'Panels are manually curated in this workspace. You can switch to an automated workspace by resetting the workspace.';
  const color: TagColorName = shouldAutoGeneratePanels ? 'cactus' : 'purple';
  const icon: IconName = shouldAutoGeneratePanels
    ? 'automated-workspace'
    : 'draft';

  return (
    <PillWithTooltip
      tooltipContent={tooltip}
      dataTest="panel-gen-pill"
      tooltipWidth="18rem">
      {responsiveSize === 'large' ? (
        <ExpandingPill label={label} color={color} icon={icon} />
      ) : (
        <IconOnlyPill
          aria-label={label}
          color={color}
          icon={icon}
          isInteractive
        />
      )}
    </PillWithTooltip>
  );
};
